import React from 'react'
import { motion } from 'framer-motion'
import { Monitor, Cloud, Check, X, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import melhoradorLogo from '../../../../assets/images/Melhorador-logo.png'
import melhoradorCloudLogo from '../../../../assets/images/logo-melhorador-cloud.png'

export default function Comparison() {
  return (
    <section className="py-24 bg-gradient-to-b from-black to-gray-900 relative">
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
            DESKTOP <span className="text-gray-600">VS</span> CLOUD
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Entenda qual versão é a ideal para o seu fluxo de trabalho e equipamento.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Desktop Column */}
          <motion.div 
            whileHover={{ scale: 1.01 }}
            className="bg-black/40 border border-cyber-blue/30 rounded-3xl overflow-hidden relative"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-cyber-blue shadow-[0_0_20px_rgba(0,243,255,0.5)]"></div>
            <div className="p-8 text-center border-b border-white/5 bg-cyber-blue/5">
              <div className="w-20 h-20 mx-auto mb-4 relative flex items-center justify-center">
                <img 
                  src={melhoradorLogo} 
                  alt="Melhorador Desktop" 
                  className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(0,243,255,0.3)]"
                />
              </div>
              <h3 className="text-2xl font-black text-white mb-1">MELHORADOR DESKTOP</h3>
              <p className="text-cyber-blue font-bold text-sm uppercase tracking-wider">Para Power Users</p>
            </div>
            
            <div className="p-8 space-y-6">
              <CompareItem text="Requer Placa de Vídeo (GPU)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Infinitas Artes (Ilimitado)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Melhore +10.000 artes/mês (Sem custo extra)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Upscale até 12X (+Modelos)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Arquivos Locais (Privacidade)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Plano Anual (+Barato)" icon={<Check className="text-cyber-blue" />} />
              <CompareItem text="Acesso em Celular" icon={<X className="text-gray-600" />} disabled />
            </div>

            <div className="p-8 bg-black/20 text-center">
              <p className="text-gray-400 text-sm mb-4">Você está na página desta versão.</p>
              <button 
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-3 rounded-lg bg-cyber-blue/10 border border-cyber-blue/30 text-cyber-blue font-bold hover:bg-cyber-blue hover:text-black transition-all"
              >
                ESCOLHER DESKTOP
              </button>
            </div>
          </motion.div>

          {/* Cloud Column */}
          <motion.div 
            whileHover={{ scale: 1.01 }}
            className="bg-black/40 border border-purple-500/30 rounded-3xl overflow-hidden relative opacity-90 hover:opacity-100 transition-opacity"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.5)]"></div>
            <div className="p-8 text-center border-b border-white/5 bg-purple-500/5">
              <div className="w-20 h-20 mx-auto mb-4 relative flex items-center justify-center">
                <img 
                  src={melhoradorCloudLogo} 
                  alt="Melhorador Cloud" 
                  className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                />
              </div>
              <h3 className="text-2xl font-black text-white mb-1">MELHORADOR CLOUD</h3>
              <p className="text-purple-500 font-bold text-sm uppercase tracking-wider">Para Flexibilidade</p>
            </div>
            
            <div className="p-8 space-y-6">
              <CompareItem text="Sem Placa de Vídeo (Roda na Nuvem)" icon={<Check className="text-purple-500" />} />
              <CompareItem text="Assinatura Mensal (Consome Tokens)" icon={<Check className="text-purple-500" />} />
              <CompareItem text="PC, Notebook, Celular e Tablet" icon={<Check className="text-purple-500" />} />
              <CompareItem text="Funciona Online (Qualquer lugar)" icon={<Check className="text-purple-500" />} />
              <CompareItem text="Upscale até 8X" icon={<Check className="text-purple-500" />} />
              <CompareItem text="Qualidade Premium (Igual Desktop)" icon={<Check className="text-purple-500" />} />
            </div>

            <div className="p-8 bg-black/20 text-center">
              <p className="text-gray-400 text-sm mb-4">Ideal se você não tem GPU potente.</p>
              <Link to="/software/melhorador-cloud">
                <button className="w-full py-3 rounded-lg bg-transparent border border-purple-500/30 text-purple-400 font-bold hover:bg-purple-500 hover:text-white transition-all flex items-center justify-center gap-2">
                  IR PARA CLOUD <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const CompareItem = ({ text, icon, disabled = false }: { text: string, icon: any, disabled?: boolean }) => (
  <div className={`flex items-center gap-4 ${disabled ? 'opacity-50' : ''}`}>
    <div className="p-1 rounded-full bg-white/5">
      {React.cloneElement(icon, { size: 16 })}
    </div>
    <span className="text-gray-300 font-medium">{text}</span>
  </div>
)
